import type { Appointment } from "./appointment.definition";
import { SearchableEntity } from "@ews-admin/global-design-system";
import { PaginationSettingsResponse } from "./search.definition";
import { Specialty } from "./specialty.definition";
import {
  CivilityEnum,
  Location,
  NotificationPrefEnum,
  UserInfo,
} from "./user.definition";

// medecine360-api refuses a slot starting sooner than this (@FutureTime, 4 h)
export const MIN_BOOKING_HOURS_AHEAD = 4;

export const DEFAULT_DOCTOR_IMAGE_MALE = "/image/manDoctor.png";
export const DEFAULT_DOCTOR_IMAGE_FEMALE = "/image/womenDoctor.png";

export type DoctorCardInfo = Omit<Doctor, "refId">;

// Contact Info structure from API
export type ContactInfo = {
  id: number;
  email: string;
  phoneNumber: string;
  location?: Location;
};

// Response type for doctor data
export type Doctor = SearchableEntity & UserInfo & DoctorInfo;
export type CreateDoctorPayload = Omit<Doctor, "id">;
export type DoctorInfo = {
  id: number;
  refId?: string;
  refNumber?: string;
  hospitalName?: string;
  averageRate?: number;
  providedServices?: ProvidedService[];
  nextAvailability?: string;
  specialties?: Specialty[];
  biography?: string;
  dateOfBirth?: string;
  careerStartYear?: string;
  doctorSpokenLanguages?: string[];
  healthcareFacilities?: HealthcareFacility[];
  diplomas?: string;
  activated?: boolean;
  disabled?: boolean;
  locations?: Location[];
  contactInfo?: ContactInfo;
  phoneNumber?: string;
};

export type HealthcareFacility = {
  id: number;
  facilityType: string;
  name: string;
  website?: string;
  location?: {
    id: number;
    address: string;
    coordinates: { lat: number; lng: number } | null;
    country: string;
    city: string;
    state: string;
    zip: string;
  };
  phoneNumber: string;
  disabled: boolean;
  specialties: {
    id: number;
    code: string;
    label: string | null;
  }[];
  isPartner: boolean;
  careTypes: {
    id: number;
    code: string;
    label: string | null;
  }[];
};

export type ProvidedServiceType =
  (typeof ProvidedServiceEnum)[keyof typeof ProvidedServiceEnum];

export const ProvidedServiceEnum = {
  ONLINE: "ONLINE",
  HOME: "HOME",
  CLINIC: "CLINIC",
};

export type Option = {
  label: string;
  value: unknown;
};

export const civilities: Option[] = [
  {
    label: "MR",
    value: CivilityEnum.MR,
  },
  {
    label: "MRS",
    value: CivilityEnum.MRS,
  },
];
export const preferenceModes: Option[] = [
  {
    label: "SMS",
    value: NotificationPrefEnum.SMS,
  },
  {
    label: "EMAIL",
    value: NotificationPrefEnum.EMAIL,
  },
];

export type ProvidedService = {
  code: ProvidedServiceType;
  price: number;
};

export type ResidencyType = "HOME" | "SECOND_HOME" | "OTHER";

export const ResidencyEnum = {
  HOME: "HOME", // Primary residence
  SECOND_HOME: "SECOND_HOME", // Secondary or vacation home
  OTHER: "OTHER", // Catch-all for undefined residency types
} as const;

export type AvailabilityInput = {
  id?: number;
  dayOfWeek: Option | undefined;
  startTime: string;
  endTime: string;
  isRecurring: boolean;
  slotDurationMinutes: number;
};
export type Availability = {
  id?: number;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  isRecurring: boolean;
  timeSlots?: TimeSlot[];
  slotDurationMinutes: number;
};

export type TimeSlot = {
  id: number;
  slotStartTime: string;
  slotEndTime: string;
  slotDate: string;
  status: (typeof TimeSlotStatusEnum)[keyof typeof TimeSlotStatusEnum];
  isBooked: boolean;
  isActive: boolean;
  isRecurring: boolean;
  healthcareFacilityId?: number;
  // Present on slots returned by the delete-by-range endpoint that could not be
  // removed because they are booked — carries the appointment that blocks deletion.
  associatedAppointment?: Appointment;
};

export const TimeSlotStatusEnum = {
  AVAILABLE: "AVAILABLE",
  BOOKED: "BOOKED",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED",
} as const;

export type DoctorSearchResponse = {
  data: Doctor[];
  paginationSettings: PaginationSettingsResponse;
};
